import { Action, createReducer, on } from '@ngrx/store'
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity'
import { SlimItemEntity } from './items.models'
import * as ItemActions from './items.actions'
import { FrontState } from 'libs/gb-front/core/data-access/src/'
import { Params } from '@angular/router'
import { environment } from 'apps/gb-front/src/environments/environment'

export const ITEMS_FEATURE_KEY = 'items'

export interface StaleState {
  pages: number[]
  filteredPages: number[]
}

export interface ItemState extends EntityState<SlimItemEntity> {
  // additional entities state properties
  showPoppers: boolean
  count: number
  order: string
  filteredItems: SlimItemEntity[]
  filters: Params
  stale: StaleState
  error: {}
}

// lazy load state
export interface State extends FrontState {
  [ITEMS_FEATURE_KEY]: ItemState
}

export const adapter: EntityAdapter<SlimItemEntity> = createEntityAdapter<SlimItemEntity>({
  selectId: (item: SlimItemEntity) => item.uuid,
  sortComparer: (a: SlimItemEntity, b: SlimItemEntity) => {
    // compare on alias when order is the same
    if (a.order === b.order) return a.alias.localeCompare(b.alias)
    return a.order < b.order ? 0 : 1
  },
})

export const initialState: ItemState = adapter.getInitialState({
  // additional entity state properties
  showPoppers: true,
  amount: 0,
  count: 0,
  order: '',
  filteredItems: [],
  filters: {},
  stale: {
    pages: [],
    filteredPages: [],
  },
  error: {},
})

export const ItemReducer = createReducer(
  initialState,
  on(ItemActions.addItem, (state, action) => adapter.addOne(action.item, state)),
  on(ItemActions.updateItem, (state, action) => adapter.updateOne(action.update, state)),
  on(ItemActions.deleteItem, (state, action) => adapter.removeOne(action.uuid, state)),
  on(ItemActions.loadItemsSuccess, ItemActions.resyncItemsSuccess, (state, action) =>
    adapter.upsertMany(getListItems(action.items), state),
  ),
  on(ItemActions.updateCount, (state, action) => ({
    ...state,
    count: action.count,
  })),
  on(ItemActions.updateOrderHash, (state, action) => ({
    ...state,
    order: action.order,
  })),
  on(ItemActions.resyncFilteredItemsSuccess, (state, action) => {
    const filteredItems = [...state.filteredItems]
    // replace block of items with updated items
    filteredItems.splice(action.offset, environment.itemsPageSize, ...action.items)
    return { ...state, filteredItems }
  }),
  on(ItemActions.updateFilters, (state, action) => ({
    ...state,
    filters: action.filters,
  })),
  on(ItemActions.removeItems, (state, action) => {
    const items: SlimItemEntity[] = [...selectAll(state)]

    // Remove all items from starting index
    items.splice(action.start)

    return adapter.setAll(items, state)
  }),
  on(ItemActions.clearItems, (state) => ({
    ...state,
    ...adapter.removeAll(state),
    count: initialState.count,
    filteredItems: initialState.filteredItems,
    stale: initialState.stale,
  })),
  on(ItemActions.clearItemsState, () => ({
    ...initialState,
  })),
  on(ItemActions.showPoppers, (state, { showPoppers }) => ({
    ...state,
    showPoppers,
  })),
  on(ItemActions.setAllItemsStale, (state) =>
    adapter.setAll(getItemsWithStaleFlag([...selectAll(state)]), {
      ...state,
      filteredItems: getItemsWithStaleFlag(state.filteredItems),
      stale: {
        ...state.stale,
        pages: getPages(state.ids),
        filteredPages: getPages(state.filteredItems),
      },
    }),
  ),
  on(ItemActions.setFilteredItemsStale, (state) => ({
    ...state,
    filteredItems: getItemsWithStaleFlag(state.filteredItems),
    stale: {
      ...state.stale,
      filteredPages: getPages(state.filteredItems),
    },
  })),
  on(ItemActions.removePageFromStale, (state, action) => ({
    ...state,
    stale: {
      ...state.stale,
      // remove resynced page from stale pages
      pages: state.stale.pages.filter((page) => page !== action.page),
    },
  })),
  on(ItemActions.removePagesFromStale, (state, action) => {
    const pages: number[] = [...state.stale.pages]

    // find index of page in stale pages state
    const index = state.stale.pages.indexOf(action.start)

    // remove stale pages from starting index
    pages.splice(index)

    return {
      ...state,
      stale: {
        ...state.stale,
        pages,
      },
    }
  }),
  on(ItemActions.removeFilteredPagesFromStale, (state, action) => ({
    ...state,
    stale: {
      ...state.stale,
      // remove resynced page from stale filtered pages
      filteredPages: state.stale.filteredPages.filter((page) => page !== action.page),
    },
  })),
  on(ItemActions.loadFilteredItemsSuccess, (state, action) => {
    const filteredItems = state.filteredItems.length ? [...state.filteredItems].concat(action.items) : action.items

    return {
      ...state,
      filteredItems,
    }
  }),
  on(ItemActions.clearStaleItems, (state) => ({
    ...state,
    stale: initialState.stale,
  })),
  on(ItemActions.clearFilteredItems, (state) => ({
    ...state,
    filteredItems: [],
    filters: {},
    stale: {
      ...state.stale,
      filteredPages: [],
    },
  })),
  on(ItemActions.clearStaleFilteredItems, (state) => ({
    ...state,
    stale: {
      ...state.stale,
      filteredPages: [],
    },
  })),
)

/**
 * Adds notListedLoaded = false flag to each item entity.
 * Required to update flag for items previously loaded but
 * not from list (i.e. searched item or directly accessed)
 */
export const getListItems = (items: SlimItemEntity[]) =>
  items.map((item) => ({ ...item, notListLoaded: false, isStale: false }))

export const getPages = (arr: string[] | number[] | SlimItemEntity[]) => {
  return arr
    .map((_, index: number) => (index % environment.itemsPageSize === 0 ? index : null))
    .filter((i): i is number => i !== null)
}

export const getItemsWithStaleFlag = (items: SlimItemEntity[]) => items.map((item) => ({ ...item, isStale: true }))

export function reducer(state: ItemState | undefined, action: Action) {
  return ItemReducer(state, action)
}

export const { selectIds, selectEntities, selectAll, selectTotal } = adapter.getSelectors()
